import { Card, CardDescription, CardHeader, CardTitle } from '@repo/ui'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { MessageSquarePlus } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { createSearchAgentThread } from '@/api/agents/searchAgent'
import { createThreadMessage } from '@/api/agents/threads'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { ChatInput } from '@/components/ChatInput'
import { PageHeader } from '@/components/PageHeader'

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/search-agent/')({
  head: () => ({ meta: [{ title: 'Search Agent - Snowvault' }] }),
  component: SearchAgentIndex
})

function SearchAgentIndex() {
  const { orgSlug, projectId } = Route.useParams()
  const projectIdNum = parseInt(projectId, 10)
  const router = useRouter()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const { userOrgProfile } = Route.useRouteContext().user
  const globalContentEnabled = userOrgProfile?.enabled_features.global_content ?? false
  const [error, setError] = useState<string | null>(null)

  const handleSend = async (message: string, includeOrgContent: boolean) => {
    setError(null)
    try {
      const title = message.length > 60 ? message.slice(0, 57) + '...' : message
      const thread = await createSearchAgentThread(projectIdNum, title)
      await createThreadMessage(projectIdNum, thread.threadId, message, includeOrgContent)
      await router.navigate({
        to: '/$orgSlug/$projectId/search-agent/$threadId',
        params: { orgSlug, projectId, threadId: String(thread.threadId) }
      })
    } catch (err) {
      Sentry.captureException(err, { extra: { userId: userOrgProfile?.uid, orgSlug, projectId: projectIdNum } })
      setError(err instanceof Error ? err.message : 'Failed to create thread')
    }
  }

  return (
    <>
      <PageHeader breadcrumbs={[{ label: projectTitle, to: projectTo }, { label: 'Search Agent' }]} />
      <div className="flex flex-1 flex-col items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader className="items-center text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <MessageSquarePlus className="size-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-xl">Start a new conversation</CardTitle>
            <CardDescription>
              We&apos;ve secured your data in Snowvault. Now harness the power of GenAI on your organisation&apos;s data
              in a safe and secure manner.
            </CardDescription>
          </CardHeader>
          <div className="px-6 pb-6">
            <ChatInput onSend={handleSend} globalContentEnabled={globalContentEnabled} />
            {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          </div>
        </Card>
      </div>
    </>
  )
}
